import { surveyMessageId, type ChatSurveyEvent } from "~shared/chatExperienceSurvey";
import { ChatSurveyRating } from "../ChatSurveyRating";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import ReactMarkdown from "react-markdown";
import Auth from "../../Auth";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../sudokit/components/Dialog";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { Text } from "../../sudokit/components/Text";
import { Textarea } from "../../sudokit/components/Textarea";
import { WithReset } from "../../sudokit/Layout/BoxReset";
import { AgenticMessageCard } from "./AgenticMessageCard";
import { branchTipMessageId, linearPathToLeaf, userBranchVariantsAtParent, userMessageIdsWithVisibleAssistantReply } from "~shared/persistentChatBranching";

/** Match `AGENTIC_WRITE_LEDGER_HISTORY_SLICE_LIMIT` in `src/ai/agenticChatEvolving/types.ts`. */
const SESSION_LEDGER_ADMIN_HISTORY_WINDOW = 120;

type Message = {
  id: string;
  role: string;
  content: string;
  metadata: any;
  parentMessageId?: string | null;
  createdAt: string;
  billing?: {
    provider: string | null;
    subprovider: string | null;
    costUsd: number | null;
  } | null;
};

type ChatDetail = {
  id: string;
  agentRuntime?: string | null;
  title: string | null;
  /** Agent-only scratchpad for series continuity (not visible to users in-app). */
  seriesNotes?: string | null;
  projectId: string;
  projectTitle: string | null;
  activeLeafMessageId?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  totalCostUsd: number | null;
  messages: Message[];
  surveys?: ChatSurveyEvent[];
};

type Props = {
  userId: string;
  chatId: string | null;
  focusMessageId?: string | null;
  onClose: () => void;
};

type DetailTab = "messages" | "session_ledger" | "watson";

export function AgenticChatDetailModal({ userId, chatId, focusMessageId = null, onClose }: Props) {
  const [chat, setChat] = useState<ChatDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [detailTab, setDetailTab] = useState<DetailTab>("messages");
  const [watsonMessages, setWatsonMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [watsonAnalyzing, setWatsonAnalyzing] = useState(false);
  const [watsonNotes, setWatsonNotes] = useState("");
  const [followUpInput, setFollowUpInput] = useState("");
  const [selectedLeafMessageId, setSelectedLeafMessageId] = useState<string | null>(null);
  const [ledgerResult, setLedgerResult] = useState<{ markdown: string | null } | undefined>();
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState<string | null>(null);

  const fetchChatDetails = useCallback(async () => {
    if (!chatId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/agentic-chats/${userId}/${chatId}`);
      if (response.success) {
        setChat(response.chat);
      } else {
        setError("Failed to fetch chat details");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to fetch chat details");
    } finally {
      setLoading(false);
    }
  }, [userId, chatId]);

  useEffect(() => {
    if (chatId) {
      fetchChatDetails();
    } else {
      setChat(null);
      setError(null);
    }
    setDetailTab("messages");
    setLedgerResult(undefined);
    setLedgerError(null);
    setLedgerLoading(false);
  }, [chatId, fetchChatDetails]);

  const branchRawMessages = useMemo(
    () =>
      (chat?.messages ?? []).map((message) => ({
        id: message.id,
        role: message.role,
        parent_message_id: message.parentMessageId ?? null,
        created_at: message.createdAt,
        metadata: message.metadata ?? null,
      })),
    [chat?.messages]
  );

  useEffect(() => {
    if (!chat) {
      setSelectedLeafMessageId(null);
      return;
    }
    if (focusMessageId && chat.messages.some((message) => message.id === focusMessageId)) {
      setSelectedLeafMessageId(branchTipMessageId(branchRawMessages as any, focusMessageId));
      return;
    }
    setSelectedLeafMessageId(chat.activeLeafMessageId ?? null);
  }, [branchRawMessages, chat, focusMessageId]);

  const messageById = useMemo(
    () => new Map((chat?.messages ?? []).map((message) => [message.id, message])),
    [chat?.messages]
  );

  const effectiveLeafMessageId = selectedLeafMessageId ?? chat?.activeLeafMessageId ?? null;

  const visibleMessages = useMemo(() => {
    if (!chat) return [];
    const branchPath = linearPathToLeaf(branchRawMessages as any, effectiveLeafMessageId);
    if (!branchPath.length) return chat.messages;
    const resolved = branchPath
      .map((row: any) => messageById.get(row.id))
      .filter((message): message is Message => !!message);
    return resolved.length > 0 ? resolved : chat.messages;
  }, [branchRawMessages, chat, effectiveLeafMessageId, messageById]);

  useEffect(() => {
    if (!focusMessageId || loading || detailTab !== "messages") return;
    if (!visibleMessages.some((message) => message.id === focusMessageId)) return;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(`admin-agentic-message-${focusMessageId}`)?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [detailTab, focusMessageId, loading, visibleMessages]);

  const userMessageIdsWithVisibleAssistantReplySet = useMemo(
    () => userMessageIdsWithVisibleAssistantReply(branchRawMessages as any),
    [branchRawMessages],
  );

  const hasBranches = useMemo(() => {
    const siblingMap = new Map<string, number>();
    for (const row of branchRawMessages as any[]) {
      if (row?.role !== "user") continue;
      const parentKey = String(row?.parent_message_id ?? "root");
      siblingMap.set(parentKey, (siblingMap.get(parentKey) ?? 0) + 1);
    }
    return [...siblingMap.values()].some((count) => count > 1);
  }, [branchRawMessages]);

  useEffect(() => {
    if (detailTab !== "session_ledger" || !userId || !chatId) {
      return;
    }

    let cancelled = false;
    setLedgerLoading(true);
    setLedgerError(null);

    (async () => {
      try {
        const response = await Auth.fetchWithIdToken(`/api/admin/agentic-chats/${userId}/${chatId}/session-ledger`, {});
        if (cancelled) return;
        if (response.success) {
          setLedgerResult({
            markdown: typeof response.ledger === "string" ? response.ledger : null,
          });
        } else {
          setLedgerError("Failed to load session ledger");
          setLedgerResult(undefined);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setLedgerError(err instanceof Error ? err.message : "Failed to load session ledger");
          setLedgerResult(undefined);
        }
      } finally {
        if (!cancelled) setLedgerLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setLedgerLoading(false);
    };
  }, [detailTab, userId, chatId]);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const analyzeChat = async (followUp?: string) => {
    if (!chat) return;
    setWatsonAnalyzing(true);
    if (!followUp) {
      setWatsonMessages([]);
    }
    setDetailTab("watson");

    const history = followUp
      ? [...watsonMessages, { role: "user" as const, content: followUp }]
      : undefined;

    if (followUp) {
      setWatsonMessages((prev) => [...prev, { role: "user", content: followUp }]);
    }

    try {
      let text = "";
      await Auth.streamWithIdToken(
        `/api/admin/agentic-chats/analyze/${userId}/${chatId}`,
        {
          userNotes: watsonNotes,
          conversationHistory: history,
        },
        null,
        (chunk: any) => {
          if (chunk.text) {
            text += chunk.text;
            setWatsonMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === "assistant") {
                return [...prev.slice(0, -1), { role: "assistant", content: text }];
              }
              return [...prev, { role: "assistant", content: text }];
            });
          }
        },
        () => { setWatsonAnalyzing(false); },
        (error: any) => {
          console.error("Watson analysis error:", error);
          setWatsonAnalyzing(false);
        },
      );
    } catch (error) {
      console.error("Watson analysis error:", error);
      setWatsonAnalyzing(false);
    }
  };

  const handleFollowUp = () => {
    if (!followUpInput.trim() || watsonAnalyzing) return;
    const question = followUpInput.trim();
    setFollowUpInput("");
    analyzeChat(question);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const surveysByMessage = useMemo(() => {
    const byMessage = new Map<string | null, ChatSurveyEvent[]>();
    for (const survey of chat?.surveys ?? []) {
      const id = surveyMessageId(survey, chat?.messages ?? []);
      byMessage.set(id, [...(byMessage.get(id) ?? []), survey]);
    }
    return byMessage;
  }, [chat]);

  const unavailableSurveys = (chat?.surveys ?? []).filter((survey) =>
    survey.lastMessageId && !messageById.has(survey.lastMessageId),
  );

  const renderSurvey = (survey: ChatSurveyEvent) => (
    <div key={survey.id} className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-gray-800" data-testid="chat-survey-event">
      <div className="flex flex-wrap items-center gap-3">
        <span className="font-medium">Chat experience</span>
        <ChatSurveyRating rating={survey.rating} reaction={survey.reaction} skipped={survey.skipped} />
        <time className="ml-auto text-xs text-gray-500" dateTime={survey.createdAt}>{new Date(survey.createdAt).toLocaleString()}</time>
      </div>
      {survey.feedbackText && <p className="mt-2 whitespace-pre-wrap">{survey.feedbackText}</p>}
    </div>
  );

  const userMessages = chat?.messages.filter((m) => m.role === "user").length || 0;
  const assistantMessages = chat?.messages.filter((m) => m.role === "assistant").length || 0;

  return (
    <Dialog
      open={!!chatId}
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent className={`${detailTab === "watson" ? "sm:max-w-[90vw]" : "sm:max-w-4xl"} h-[85vh] flex flex-col overflow-hidden p-0`}>
        <WithReset className="flex flex-col h-full text-left px-6 pt-6">
          <DialogHeader className="flex-shrink-0 pb-4 border-b border-gray-200 text-left">
            <DialogTitle className="text-left">{chat?.title || "Untitled Chat"}</DialogTitle>
            {chat && (
              <div className="flex flex-wrap items-center gap-2 mt-2 text-left">
                <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                  {chat.projectTitle || chat.projectId}
                </span>
                <Text size="sm" color="grey-1" align="left">|</Text>
                <span
                  className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium"
                  title="The chat's assigned runtime. Each turn's Raw Metadata records the runtime that actually executed, including rollback overrides."
                >
                  Runtime: {chat.agentRuntime === "evolving-v1" ? "v2 (evolving-v1)" : chat.agentRuntime === "master-v1" ? "v1 (master-v1)" : chat.agentRuntime || "Unassigned"}
                </span>
                <Text size="sm" color="grey-1" align="left">|</Text>
                <Text size="sm" color="grey-1" align="left">
                  {chat.messages.length} messages ({userMessages} user, {assistantMessages} assistant)
                </Text>
                <Text size="sm" color="grey-1" align="left">|</Text>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded text-xs font-mono font-medium">
                  Thread cost: {chat.totalCostUsd != null ? `$${chat.totalCostUsd.toFixed(4)}` : "—"}
                </span>
                {hasBranches && (
                  <>
                    <Text size="sm" color="grey-1" align="left">|</Text>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                      Threaded chat
                    </span>
                  </>
                )}
                <Text size="sm" color="grey-1" align="left">|</Text>
                <Text size="sm" color="grey-1" align="left">Created: {formatDate(chat.createdAt)}</Text>
                {chat.deletedAt && (
                  <>
                    <Text size="sm" color="grey-1" align="left">|</Text>
                    <Text size="sm" color="danger-color" weight="medium" align="left">
                      Deleted: {formatDate(chat.deletedAt)}
                    </Text>
                  </>
                )}
                {detailTab === "watson" && (
                  <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                    Watson Analysis
                  </span>
                )}
                {detailTab === "session_ledger" && (
                  <span className="px-2 py-1 bg-slate-200 text-slate-800 rounded text-xs font-medium">
                    Session edit ledger
                  </span>
                )}
              </div>
            )}
          </DialogHeader>

          {chat && (
            <div className="flex-shrink-0 mb-4 p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-left">
              <Text size="sm" weight="medium" align="left" className="mb-1">
                Hidden series notes (agent scratchpad)
              </Text>
              <Text size="xs" color="grey-1" align="left" className="mb-2">
                Not shown to users in Story Chat. The agent may write here via tools when answering series continuity questions.
              </Text>
              <pre className="text-xs whitespace-pre-wrap font-mono text-gray-800 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded p-2">
                {(chat.seriesNotes && chat.seriesNotes.trim()) ? chat.seriesNotes : "— empty —"}
              </pre>
            </div>
          )}

          <div
            role="tablist"
            aria-label="Chat inspector panels"
            className="flex flex-wrap gap-2 flex-shrink-0 mb-4"
          >
            <SudoButton
              type="button"
              role="tab"
              aria-selected={detailTab === "messages"}
              aria-controls="admin-agentic-chat-panel-messages"
              id="admin-agentic-chat-tab-messages"
              variant={detailTab === "messages" ? "primary" : "tertiaryGrey"}
              size="small"
              onClick={() => setDetailTab("messages")}
            >
              Messages
            </SudoButton>
            <SudoButton
              type="button"
              role="tab"
              aria-selected={detailTab === "session_ledger"}
              aria-controls="admin-agentic-chat-panel-session-ledger"
              id="admin-agentic-chat-tab-session-ledger"
              variant={detailTab === "session_ledger" ? "primary" : "tertiaryGrey"}
              size="small"
              onClick={() => setDetailTab("session_ledger")}
            >
              Session ledger
            </SudoButton>
            <SudoButton
              type="button"
              role="tab"
              aria-selected={detailTab === "watson"}
              aria-controls="admin-agentic-chat-panel-watson"
              id="admin-agentic-chat-tab-watson"
              variant={detailTab === "watson" ? "primary" : "tertiaryGrey"}
              size="small"
              onClick={() => setDetailTab("watson")}
            >
              Watson
            </SudoButton>
          </div>

          {detailTab === "watson" ? (
            <div
              id="admin-agentic-chat-panel-watson"
              role="tabpanel"
              aria-labelledby="admin-agentic-chat-tab-watson"
              className="flex-1 flex flex-col min-h-0 overflow-hidden"
            >
              <style dangerouslySetInnerHTML={{ __html: `
                .watson-markdown p {
                  margin-bottom: 1rem !important;
                  line-height: 1.7 !important;
                }
                .watson-markdown ul, .watson-markdown ol {
                  padding-left: 1.5rem !important;
                  margin-bottom: 1rem !important;
                  margin-top: 0.5rem !important;
                }
                .watson-markdown ul { list-style-type: disc !important; }
                .watson-markdown ol { list-style-type: decimal !important; }
                .watson-markdown li {
                  margin-bottom: 0.5rem !important;
                  line-height: 1.7 !important;
                  padding-left: 0.25rem !important;
                }
                .watson-markdown ul ul { list-style-type: circle !important; margin-top: 0.25rem !important; }
                .watson-markdown li p { margin-bottom: 0.25rem !important; margin-top: 0 !important; display: inline !important; }
                .watson-markdown h1, .watson-markdown h2, .watson-markdown h3,
                .watson-markdown h4, .watson-markdown h5, .watson-markdown h6 {
                  margin-top: 1.75rem !important;
                  margin-bottom: 0.75rem !important;
                  font-weight: 600 !important;
                  line-height: 1.4 !important;
                }
                .watson-markdown h2 { font-size: 1.25rem !important; }
                .watson-markdown h3 { font-size: 1.1rem !important; }
                .watson-markdown strong { font-weight: 700 !important; color: rgb(17 24 39) !important; }
                .watson-markdown code {
                  background-color: rgb(243 244 246) !important;
                  padding: 0.125rem 0.25rem !important;
                  border-radius: 0.25rem !important;
                  font-size: 0.875em !important;
                }
              ` }} />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden py-4">
                <div className="flex flex-col gap-4 overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CS Investigation Notes
                    </label>
                    <Textarea
                      value={watsonNotes}
                      onChange={(e) => setWatsonNotes(e.target.value)}
                      placeholder="Describe what the user reported, what went wrong..."
                      mode="default"
                      className="w-full min-h-[120px]"
                      short
                    />
                  </div>
                  <SudoButton
                    variant="primary"
                    size="small"
                    onClick={() => analyzeChat()}
                    disabled={watsonAnalyzing}
                  >
                    {watsonAnalyzing ? "Watson is investigating..." : "Run Watson"}
                  </SudoButton>
                </div>

                <div className="col-span-2 flex flex-col overflow-hidden bg-gray-50 rounded-lg">
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {watsonMessages.length > 0 ? (
                      watsonMessages.map((msg, i) => (
                        <div key={i} className={msg.role === "user" ? "flex justify-end" : ""}>
                          {msg.role === "user" ? (
                            <div className="bg-blue-100 text-blue-900 rounded-lg px-4 py-2 max-w-[80%] text-sm">
                              {msg.content}
                            </div>
                          ) : (
                            <div className="prose prose-sm max-w-none watson-markdown">
                              <ReactMarkdown>{msg.content}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-500">
                        <p>{watsonAnalyzing
                          ? "Watson is investigating..."
                          : "Add notes and run Watson to analyze this chat"
                        }</p>
                      </div>
                    )}
                  </div>

                  {watsonMessages.length > 0 && (
                    <div className="flex-shrink-0 border-t border-gray-200 p-3 flex gap-2">
                      <input
                        type="text"
                        value={followUpInput}
                        onChange={(e) => setFollowUpInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleFollowUp()}
                        placeholder="Ask Watson a follow-up question..."
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400"
                        disabled={watsonAnalyzing}
                      />
                      <SudoButton
                        variant="primary"
                        size="small"
                        onClick={handleFollowUp}
                        disabled={watsonAnalyzing || !followUpInput.trim()}
                      >
                        Ask
                      </SudoButton>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : detailTab === "session_ledger" ? (
            <div
              id="admin-agentic-chat-panel-session-ledger"
              role="tabpanel"
              aria-labelledby="admin-agentic-chat-tab-session-ledger"
              className="flex-1 overflow-y-auto py-4 text-left -mr-2 pr-2 [scrollbar-width:thin] [scrollbar-color:rgba(156,163,175,0.3)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/30"
            >
              <Text size="sm" color="grey-1" align="left" className="mb-3 block">
                Same compaction-resistant summary the write-mode agent receives as a hidden system message (uses the latest{" "}
                {SESSION_LEDGER_ADMIN_HISTORY_WINDOW} messages, matching production defaults). End users do not see this in Story Chat UI.
              </Text>
              {ledgerLoading && (
                <div className="text-center py-8">
                  <Text size="sm" color="grey-1" align="center">Loading ledger…</Text>
                </div>
              )}
              {ledgerError && (
                <div className="text-center py-8">
                  <Text size="sm" color="danger-color" align="center">{ledgerError}</Text>
                </div>
              )}
              {!ledgerLoading && !ledgerError && ledgerResult !== undefined && (
                <pre className="text-xs whitespace-pre-wrap font-mono text-gray-800 bg-white border border-gray-200 rounded p-3 min-h-[120px]">
                  {ledgerResult.markdown && ledgerResult.markdown.trim().length > 0
                    ? ledgerResult.markdown
                    : "(empty — no ledger rows for this slice)"}
                </pre>
              )}
            </div>
          ) : (
            <div
              id="admin-agentic-chat-panel-messages"
              role="tabpanel"
              aria-labelledby="admin-agentic-chat-tab-messages"
              className="flex-1 overflow-y-auto py-4 space-y-4 text-left -mr-2 pr-2 [scrollbar-width:thin] [scrollbar-color:rgba(156,163,175,0.3)_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-400/30"
            >
              {loading && (
                <div className="text-center py-8">
                  <Text size="sm" color="grey-1" align="center">Loading chat details...</Text>
                </div>
              )}

              {error && (
                <div className="text-center py-8">
                  <Text size="sm" color="danger-color" align="center">{error}</Text>
                </div>
              )}

              {chat && chat.messages.length === 0 && (
                <div className="text-center py-8">
                  <Text size="sm" color="grey-1" align="center">No messages in this chat.</Text>
                </div>
              )}

              {(surveysByMessage.get(null) ?? []).map(renderSurvey)}
              {chat &&
                visibleMessages.map((message) => {
                  const branchVariants =
                    message.role === "user"
                      ? userBranchVariantsAtParent(branchRawMessages as any, message.parentMessageId ?? null).filter(
                          (candidate) =>
                            candidate.id === message.id
                            || userMessageIdsWithVisibleAssistantReplySet.has(candidate.id)
                        )
                      : [];
                  const branchIdx = branchVariants.findIndex((candidate) => candidate.id === message.id);
                  const branchCount = branchVariants.length;

                  return (
                    <Fragment key={message.id}>
                      <AgenticMessageCard
                        message={message}
                        highlighted={focusMessageId === message.id}
                        isBranchedMessage={message.role === "user" && branchCount > 1}
                        userBranchSiblingIndex={branchCount > 1 && branchIdx >= 0 ? branchIdx : undefined}
                        userBranchSiblingCount={branchCount > 1 ? branchCount : undefined}
                        onUserBranchPrev={
                          branchCount > 1 && branchIdx > 0
                            ? () => {
                                const prev = branchVariants[branchIdx - 1];
                                const tip = branchTipMessageId(branchRawMessages as any, prev.id);
                                setSelectedLeafMessageId(tip);
                              }
                            : undefined
                        }
                        onUserBranchNext={
                          branchCount > 1 && branchIdx >= 0 && branchIdx < branchCount - 1
                            ? () => {
                                const next = branchVariants[branchIdx + 1];
                                const tip = branchTipMessageId(branchRawMessages as any, next.id);
                                setSelectedLeafMessageId(tip);
                              }
                            : undefined
                        }
                      />
                      {(surveysByMessage.get(message.id) ?? []).map(renderSurvey)}
                    </Fragment>
                  );
                })}
              {unavailableSurveys.length > 0 && (
                <section className="space-y-3 border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-600">Ratings for unavailable messages</h3>
                  {unavailableSurveys.map(renderSurvey)}
                </section>
              )}
            </div>
          )}

          <DialogFooter className="flex-shrink-0 pt-4 pb-6 border-t border-gray-200 mt-auto">
            <CopyToClipboard
              text={JSON.stringify(chat, null, 2)}
              onCopy={handleCopy}
            >
              <SudoButton
                variant="tertiaryGrey"
                size="small"
                disabled={!chat}
              >
                {copied ? "Copied!" : "Copy JSON"}
              </SudoButton>
            </CopyToClipboard>
            <SudoButton
              variant="secondary"
              size="small"
              onClick={onClose}
            >
              Close
            </SudoButton>
          </DialogFooter>
        </WithReset>
      </DialogContent>
    </Dialog>
  );
}
